import React, { useEffect, useRef, useState } from 'react';
import { ItfApiForm } from 'itf_formbuilder_react';
import { useLocation } from 'react-router-dom';
import { __ } from '../../services/translation';
import {
    clearUnsavedChanges,
    resolveUnsavedFieldLabel,
    updateUnsavedField,
} from '../../services/unsaved-changes';

type ItfFormProps = React.ComponentProps<typeof ItfApiForm>;

interface Props extends ItfFormProps {
    unsavedScope?: 'page' | 'modal';
    unsavedFormId?: string;
    unsavedGuardPath?: string;
    normalizeFieldValue?: (field: any, value: any) => any;
}

let nextFormInstanceId = 0;

const TrackedItfApiForm = React.forwardRef<any, Props>(({
    formId,
    unsavedScope = 'page',
    unsavedFormId,
    unsavedGuardPath,
    normalizeFieldValue,
    onTouch,
    onSavedSuccessfully,
    ...formProps
}: Props, forwardedRef) => {
    const location = useLocation();
    const [instanceId] = useState(() => ++nextFormInstanceId);
    const previousRegistryId = useRef<string | null>(null);
    const registryId =
        unsavedFormId ||
        `${unsavedScope}:${formId}:${location.pathname}:${instanceId}`;
    const guardPath = unsavedGuardPath || location.pathname;

    useEffect(() => {
        const previousId = previousRegistryId.current;
        if (previousId && previousId !== registryId) {
            clearUnsavedChanges(previousId);
        }
        previousRegistryId.current = registryId;

        return () => clearUnsavedChanges(registryId);
    }, [registryId]);

    return (
        <ItfApiForm
            key={registryId}
            ref={forwardedRef}
            {...formProps}
            formId={formId}
            onTouch={(field: any, currentValue: any) => {
                updateUnsavedField(
                    registryId,
                    field.id,
                    normalizeFieldValue ? normalizeFieldValue(field, field.value) : field.value,
                    normalizeFieldValue ? normalizeFieldValue(field, currentValue) : currentValue,
                    resolveUnsavedFieldLabel(field.title, field.placeholder, field.id, __),
                    unsavedScope,
                    guardPath
                );
                if (onTouch) onTouch(field, currentValue);
            }}
            onSavedSuccessfully={(...args: any[]) => {
                clearUnsavedChanges(registryId);
                if (onSavedSuccessfully) onSavedSuccessfully(...args);
            }}
        />
    );
});

export default TrackedItfApiForm;
